import { type ChatMetricsObserver, ChatMetricsProvider } from "@qw/chat";
import { type PropsWithChildren, useEffect } from "react";

import {
  captureLongAnimationFrame,
  captureMainThreadStall,
  clearQwibTranscriptDimensions,
  type LongAnimationFrameEntry,
  recordQwibTranscriptDimensions,
} from "./qwib-telemetry";

/**
 * A long task is anything over 50ms, which a healthy session produces constantly. A full second of
 * frozen main thread is the point where the app stops looking slow and starts looking broken — the
 * reading B-2012 needs, and the one a 50ms floor would bury.
 */
export const MAIN_THREAD_STALL_THRESHOLD_MS = 1_000;

/**
 * A session that stalls once tends to stall repeatedly, and B-2012's replay already showed PostHog
 * rate-limiting this user. One event per window characterizes the session without the emitter
 * becoming its own source of main-thread work.
 */
export const MAIN_THREAD_STALL_COOLDOWN_MS = 30_000;

const isEntryTypeObservable = (entryType: string) =>
  typeof PerformanceObserver !== "undefined" && PerformanceObserver.supportedEntryTypes?.includes(entryType);

/**
 * Observe one entry type for as long as the app is mounted, reporting at most one entry per cooldown
 * window. Each caller keeps its own window on purpose: `longtask` and `long-animation-frame` both
 * fire for the same freeze, so a shared window would let the task reading suppress the attribution
 * for it.
 */
function useStallTelemetry(entryType: string, report: (entry: PerformanceEntry) => void): void {
  useEffect(() => {
    if (!isEntryTypeObservable(entryType)) {
      return;
    }

    let lastEmittedAt = 0;

    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.duration < MAIN_THREAD_STALL_THRESHOLD_MS) {
          continue;
        }

        const now = Date.now();
        if (now - lastEmittedAt < MAIN_THREAD_STALL_COOLDOWN_MS) {
          continue;
        }

        lastEmittedAt = now;
        report(entry);
      }
    });

    // `buffered` so a stall during bootstrap — before this effect could run — is still reported.
    observer.observe({ type: entryType, buffered: true });

    return () => observer.disconnect();
  }, [entryType, report]);
}

const reportMainThreadStall = (entry: PerformanceEntry) => captureMainThreadStall(entry.duration);

/**
 * TypeScript's DOM lib has no `long-animation-frame` entry as of 5.9, and `getEntries()` is typed as
 * the base entry either way. `scripts` is the field that tells one apart from every other entry
 * type, so it is what the narrowing checks.
 */
const isLongAnimationFrameEntry = (entry: PerformanceEntry): entry is PerformanceEntry & LongAnimationFrameEntry =>
  "scripts" in entry && Array.isArray(entry.scripts);

const reportLongAnimationFrame = (entry: PerformanceEntry) => {
  if (isLongAnimationFrameEntry(entry)) {
    captureLongAnimationFrame(entry);
  }
};

/**
 * Report main-thread stalls for as long as the app is mounted, from both angles: `longtask` says the
 * renderer froze and for how long, `long-animation-frame` says which script did it. Chromium-only —
 * neither entry type has a Safari or Firefox implementation, and `long-animation-frame` needs 123+ —
 * so this characterizes the surface rather than covering it.
 */
export function useMainThreadStallTelemetry(): void {
  useStallTelemetry("longtask", reportMainThreadStall);
  useStallTelemetry("long-animation-frame", reportLongAnimationFrame);
}

const qwibChatMetricsObserver: ChatMetricsObserver = {
  onTranscriptDimensions: recordQwibTranscriptDimensions,
  onSessionEnd: clearQwibTranscriptDimensions,
};

/**
 * QWIB's side of the `@qw/chat` measurement seam, plus the main-thread stall observers. Both sinks
 * live here so `@qw/chat` never learns that PostHog exists (B-2020).
 */
export const QwibTelemetryProvider = ({ children }: PropsWithChildren) => {
  useMainThreadStallTelemetry();

  return <ChatMetricsProvider observer={qwibChatMetricsObserver}>{children}</ChatMetricsProvider>;
};
